import React, { useState } from 'react';
import { 
    LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    AreaChart, Area, ComposedChart, Scatter
} from 'recharts';
import './PrebuiltReports.css';

const PrebuiltReports = ({ dateRange, comparisonPeriod }) => {
    const [activeReport, setActiveReport] = useState('advocacy_campaign');

    // Updated real political campaign data
    const advocacyFunnelData = [
        { stage: 'Total Universe', count: 28000, conversion: 100 },
        { stage: 'Records Contacted', count: 9632, conversion: 34.4 },
        { stage: 'Completed Calls', count: 7177, conversion: 74.5 },
        { stage: 'Agreed With Position', count: 1542, conversion: 21.5 },
        { stage: 'Patched Through', count: 1135, conversion: 73.6 }
    ];

    // Call outcome data with Patched and Agreed Not Patched swapped
    const callOutcomeData = [
        { status: 'Patched', value: 1135 },           // Swapped position
        { status: 'Disagreed', value: 330 },
        { status: 'Undecided', value: 678 },
        { status: 'Agreed, Not Patched', value: 407 },  // Swapped position
        { status: 'Refused/Hang-up', value: 4627 }
    ];
    
    // District performance data (derived from the real data)
    const districtPerformanceData = [
        { district: 'District 1', total_calls: 1820, patch_rate: 12.5, support_rate: 22.1, conversion: 61.7 },
        { district: 'District 2', total_calls: 1685, patch_rate: 10.8, support_rate: 19.8, conversion: 58.2 },
        { district: 'District 3', total_calls: 2370, patch_rate: 14.2, support_rate: 24.5, conversion: 68.9 },
        { district: 'District 4', total_calls: 1302, patch_rate: 8.9, support_rate: 16.2, conversion: 52.3 },
        { district: 'District 5', total_calls: 2455, patch_rate: 11.8, support_rate: 20.7, conversion: 64.5 }
    ];
    
    // Hourly breakdown of patch-through calls
    const hourlyCallData = [
        { hour: '9AM', patches: 142, support_rate: 22.8 },
        { hour: '10AM', patches: 167, support_rate: 24.2 },
        { hour: '11AM', patches: 186, support_rate: 25.7 },
        { hour: '12PM', patches: 148, support_rate: 21.6 },
        { hour: '1PM', patches: 121, support_rate: 19.4 },
        { hour: '2PM', patches: 135, support_rate: 20.8 },
        { hour: '3PM', patches: 143, support_rate: 21.5 },
        { hour: '4PM', patches: 93, support_rate: 18.2 }
    ];

    const renderActiveReport = () => {
        // Update to purple shades for the funnel bars
        const purpleShades = [
            "#E9D8FD", // Lightest purple for Total Universe
            "#D6BCFA", 
            "#B794F4", 
            "#9F7AEA", 
            "#9b51e0"  // Main app purple for Patched Through
        ];
        
        // Updated colors for pie chart to match app theme
        const CHART_COLORS = [
            '#9b51e0', // Main app purple for Patched
            '#6A67CE', // Complementary blue-purple
            '#F67280', // Complementary pink
            '#53B8BB', // Complementary teal
            '#404B69'  // Dark blue-gray
        ];
        
        switch(activeReport) {
            case 'advocacy_campaign':
                return (
                    <div className="report-container">
                        <div className="report-header">
                            <h2>Legislative Advocacy Campaign Impact</h2>
                            <p className="report-description">
                                Comprehensive analysis of our voter outreach and legislative office patch-through campaign
                            </p>
                        </div>
                        <div className="charts-grid">
                            <div className="chart-container">
                                <h3>Advocacy Funnel</h3>
                                <ResponsiveContainer width="100%" height={400}>
                                    <BarChart
                                        data={advocacyFunnelData}
                                        layout="vertical"
                                        margin={{ left: 20, right: 30, top: 20, bottom: 20 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis type="number" />
                                        <YAxis 
                                            dataKey="stage" 
                                            type="category"
                                            width={120}
                                        />
                                        <Tooltip formatter={(value) => Number(value).toLocaleString()} />
                                        <Legend />
                                        <Bar 
                                            dataKey="count" 
                                            name="Voter Count"
                                        >
                                            {advocacyFunnelData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={purpleShades[index]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="chart-container">
                                <h3>Call Outcomes Distribution</h3>
                                <ResponsiveContainer width="100%" height={400}>
                                    <PieChart>
                                        <Pie
                                            data={callOutcomeData}
                                            dataKey="value"
                                            nameKey="status"
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={150}
                                            label={({ name, percent }) => `${percent * 100 > 3 ? name : ''} ${(percent * 100).toFixed(1)}%`}
                                            labelLine={percent => percent * 100 > 3}
                                        >
                                            {callOutcomeData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip 
                                            formatter={(value) => Number(value).toLocaleString()} 
                                            contentStyle={{ fontSize: '14px' }}
                                            wrapperClassName="larger-tooltip"
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        <div className="campaign-insights-section">
                            <h3 className="campaign-insights-title">Campaign Insights</h3>
                            
                            <div className="insights-grid" style={{ marginTop: '10px' }}>
                                <div className="insight-card">
                                    <div className="insight-title">Patch-Through Rate</div>
                                    <div className="insight-value">73.6%</div>
                                    <div className="insight-desc">of supporters were successfully patched to legislator offices</div>
                                </div>
                                <div className="insight-card">
                                    <div className="insight-title">Support Rate</div>
                                    <div className="insight-value">21.5%</div>
                                    <div className="insight-desc">of completed calls agreed with our position</div>
                                </div>
                                <div className="insight-card">
                                    <div className="insight-title">Overall Penetration</div>
                                    <div className="insight-value">34.4%</div>
                                    <div className="insight-desc">of the targeted universe was contacted</div>
                                </div>
                                <div className="insight-card">
                                    <div className="insight-title">Conversion Efficiency</div>
                                    <div className="insight-value">4.1%</div>
                                    <div className="insight-desc">of total universe patched to legislators</div>
                                </div>
                            </div>
                        </div>
                        <div className="data-table-section">
                            <h3>Detailed Campaign Data</h3>
                            <div className="data-table-container">
                                <table className="campaign-table">
                                    <thead>
                                        <tr>
                                            <th>Stage</th>
                                            <th>Count</th>
                                            <th>Conversion Rate (%)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {advocacyFunnelData.map((row, index) => (
                                            <tr key={index}>
                                                <td>{row.stage}</td>
                                                <td>{row.count.toLocaleString()}</td>
                                                <td>{row.conversion}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                );
                
            case 'district_analysis':
                return (
                    <div className="report-container">
                        <div className="report-header">
                            <h2>District-Level Performance Analysis</h2>
                            <p className="report-description">
                                Geographic and temporal analysis of voter sentiment and patch-through metrics
                            </p>
                        </div>
                        <div className="charts-grid">
                            <div className="chart-container">
                                <h3>District Performance Comparison</h3>
                                <ResponsiveContainer width="100%" height={400}>
                                    <BarChart data={districtPerformanceData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="district" />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend />
                                        <Bar dataKey="support_rate" name="Support Rate (%)" fill="#9b51e0" />
                                        <Bar dataKey="patch_rate" name="Patch-Through Rate (%)" fill="#6A67CE" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="chart-container">
                                <h3>Hourly Patch-Through Volume</h3>
                                <ResponsiveContainer width="100%" height={400}>
                                    <ComposedChart data={hourlyCallData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="hour" />
                                        <YAxis yAxisId="left" />
                                        <YAxis yAxisId="right" orientation="right" />
                                        <Tooltip />
                                        <Legend />
                                        <Bar yAxisId="left" dataKey="patches" name="Patch-Through Calls" fill="#4A7FB9" />
                                        <Line 
                                            yAxisId="right"
                                            type="monotone" 
                                            dataKey="support_rate" 
                                            name="Support Rate (%)"
                                            stroke="#D22532"
                                            strokeWidth={2}
                                        />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        <div className="voter-response-section">
                            <h3>Voter Response Analysis</h3>
                            <div className="question-response">
                                <div className="question-text">
                                    "Do you agree that the Senate ought to consider and pass Oklahoma HB 3214?"
                                </div>
                                <div className="response-grid">
                                    <div className="response-card">
                                        <div className="response-value">21.5%</div>
                                        <div className="response-label">Yes (1,542)</div>
                                    </div>
                                    <div className="response-card">
                                        <div className="response-value">4.6%</div>
                                        <div className="response-label">No (330)</div>
                                    </div>
                                    <div className="response-card">
                                        <div className="response-value">9.4%</div>
                                        <div className="response-label">Undecided (678)</div>
                                    </div>
                                    <div className="response-card">
                                        <div className="response-value">64.5%</div>
                                        <div className="response-label">Refused (4,627)</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="data-table-section">
                            <h3>District Performance Data</h3>
                            <div className="data-table-container">
                                <table className="campaign-table">
                                    <thead>
                                        <tr>
                                            <th>District</th>
                                            <th>Total Calls</th>
                                            <th>Support Rate (%)</th>
                                            <th>Patch-Through Rate (%)</th>
                                            <th>Conversion Rate (%)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {districtPerformanceData.map((row, index) => (
                                            <tr key={index}>
                                                <td>{row.district}</td>
                                                <td>{row.total_calls.toLocaleString()}</td>
                                                <td>{row.support_rate.toFixed(1)}%</td>
                                                <td>{row.patch_rate.toFixed(1)}%</td>
                                                <td>{row.conversion.toFixed(1)}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="prebuilt-reports">
            <div className="reports-sidebar">
                <div className="sidebar-header">
                    <h3>Campaign Reports</h3>
                    <p>Comprehensive political advocacy analytics</p>
                </div>
                <div className="report-templates">
                    <button 
                        className={`template-btn ${activeReport === 'advocacy_campaign' ? 'active' : ''}`}
                        onClick={() => setActiveReport('advocacy_campaign')}
                    >
                        <span className="template-icon">🏛️</span>
                        <div className="template-info">
                            <span className="template-name">Advocacy Campaign Impact</span>
                            <span className="template-desc">Patch-through performance and conversion funnel</span>
                        </div>
                    </button>
                    <button 
                        className={`template-btn ${activeReport === 'district_analysis' ? 'active' : ''}`}
                        onClick={() => setActiveReport('district_analysis')}
                    >
                        <span className="template-icon">🗺️</span>
                        <div className="template-info">
                            <span className="template-name">District Analysis</span>
                            <span className="template-desc">Geographic and temporal response patterns</span>
                        </div>
                    </button>
                </div>
            </div>
            <div className="report-content">
                {renderActiveReport()}
            </div>
        </div>
    );
};

export default PrebuiltReports; 